import { pickT } from "@repo/shared";
import { useMemo } from "react";
import { PieChart } from "@/components/charts/pie-chart";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ComponentStyleType } from "@/lib/types/flow";
import { getRelativeTime } from "@/lib/utils";
import { TABLEAU20_COLORS } from "@/lib/utils/vega-chart-shared";
import { T } from "./asset-allocation-chart.i18n";

type AssetAllocationChartProps = {
  data: Array<{
    assetClassId: string;
    assetClassName: string;
    percentage: number;
  }>;
  unclassifiedPct?: number;
  height?: number;
  timestamp?: string;
  showChart?: boolean;
  showTable?: boolean;
  chartColors?: string[] | null;
  // Configurable styling props
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  // Language for translations
  language?: string;
};

export function AssetAllocationChart({
  data,
  unclassifiedPct = 0,
  height = 280,
  timestamp,
  showChart = true,
  showTable = false,
  chartColors,
  style = "none",
  title,
  description,
  language = "en_GB",
}: AssetAllocationChartProps) {
  const t = pickT(T, language);
  const colors = chartColors || TABLEAU20_COLORS;

  // Get container class based on style
  const getContainerClass = () => {
    switch (style) {
      case "border":
        return "border rounded-lg p-6 space-y-4";
      case "box":
        return "rounded-lg p-6 space-y-4 bg-muted/50";
      default:
        return "space-y-4";
    }
  };
  // Memoize chart data, sorted data, pie data, and color styles
  const { sortedData, pieData, colorStyles } = useMemo(() => {
    // Combine data with unclassified if > 0
    const chartData = [...data];
    if (unclassifiedPct > 0) {
      chartData.push({
        assetClassId: "unclassified",
        assetClassName: t.unclassified,
        percentage: unclassifiedPct,
      });
    }

    // Sort by percentage (largest to smallest)
    const sorted = [...chartData].sort((a, b) => b.percentage - a.percentage);

    // Transform for PieChart component
    const pie = sorted.map((item) => ({
      id: item.assetClassId,
      label: item.assetClassName,
      value: item.percentage,
    }));

    // Pre-compute color styles for table rows
    const styles = sorted.map((_, index) => ({
      backgroundColor: colors[index % colors.length],
    }));

    return { sortedData: sorted, pieData: pie, colorStyles: styles };
  }, [data, unclassifiedPct, colors, t.unclassified]);

  return (
    <div className={`w-full ${getContainerClass()}`}>
      <FlowComponentHeader title={title} description={description} />

      {showChart && (
        <div style={{ height }}>
          <PieChart
            data={pieData}
            width={350}
            height={height}
            innerRadius={0.5}
            chartColors={colors}
            lang={language}
          />
        </div>
      )}
      {showTable && (
        <Table variant="data">
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead>{t.assetClass}</TableHead>
              <TableHead className="text-right">{t.allocation}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.map((item, index) => (
              <TableRow key={item.assetClassId}>
                <TableCell>
                  <div className="w-4 h-4 rounded-sm" style={colorStyles[index]} />
                </TableCell>
                <TableCell>{item.assetClassName}</TableCell>
                <TableCell className="text-right">{item.percentage.toFixed(1)}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {timestamp && (
        <div className="text-xs text-muted-foreground">
          {t.snapshotTaken(getRelativeTime(timestamp, language))}
        </div>
      )}
    </div>
  );
}
